import { DatePickerPopover, Input, Label, Modal, RelativeDateRange, Select, SubmitButton } from "@xola/ui-kit";
import {
    DateFormat,
    formatDate,
    FormField,
    InputField,
    now,
    parseTime,
    requiredNumber,
    requiredNumberMoreThanZero,
    requiredString,
    utcToDate,
} from "common";
import dayjs, { Dayjs } from "dayjs";
import { FormikValues } from "formik";
import { isEmpty } from "lodash";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { Cron, Report } from "types";
import { object } from "yup";
import { schedulerApi } from "../../../api/schedulerApi";
import { xolaUri } from "../../../api/xolaUri";
import { useSeller, useUser } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { getQueryParams } from "../../../models/ReportFilterState";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { TimeslotTimePicker } from "../../PurchaseModification/TimeslotTimePicker";
import { DateRange } from "./CustomReportDateRangePicker";
import { ReportFilterState } from "./ReportFilterState";

enum DurationUnit {
    YEAR = "Y",
    MONTH = "M",
    WEEK = "W",
    DAY = "D",
}

export const Duration = {
    D: "Day",
    W: "Week",
    M: "Month",
    Y: "Year",
};

interface ScheduleReportModalProps {
    isOpen: boolean;
    report: Report;
    selectedDateField: string;
    onClose: () => void;
    filter: ReportFilterState;
    cron: Cron;
    onDateChange: (value: DateRange) => void;
}

export const ScheduleReportModal = ({
    isOpen,
    report,
    selectedDateField,
    onClose,
    filter,
    cron,
    onDateChange,
}: ScheduleReportModalProps) => {
    const seller = useSeller();
    const user = useUser();
    const invalidate = useInvalidate();
    const [firstStep, setFirstStep] = useState(true);
    const [dateRange, setDateRange] = useState<DateRange | null>(null);
    const [rangeName, setRangeName] = useState<string>("");

    const { mutateAsync: scheduleReport, isLoading: isSaving } = useMutation(async (values: FormikValues) => {
        const rangeNameFromCron = cron ? new URLSearchParams(cron.url).get(`${selectedDateField}[period]`) : null;

        const params: Record<string, string> = {
            ...getQueryParams(filter),
            format: "xlsx",
            seller: seller.id,
            [`${selectedDateField}[period]`]: rangeNameFromCron ?? rangeName,
            reportTitle: report.title,
        };

        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete params[`${selectedDateField}[range]`];

        const { hours, minutes } = parseTime(values.time);
        const start = dayjs(values.start).hour(hours).minute(minutes);
        const startWithoutTimezone = formatDate(start, DateFormat.DATE_TIME_ISO_WITHOUT_TIMEZONE);
        const formattedUTCDate = now(startWithoutTimezone).utc(); // Scheduler API requires a UTC timestamp

        const payload = {
            remoteId: report.id,
            remoteSystem: "xola",
            interval: `P${values.interval}${values.intervalType}`,
            createdBy: user.id,
            startAt: formattedUTCDate,
            url: `${xolaUri}/api/${report.collection}?${new URLSearchParams(params).toString()}`,
        };

        if (cron?.id) {
            return schedulerApi.put(`/crons/${cron.id}`, payload);
        }

        return schedulerApi.post(`/crons`, payload);
    });

    const handleSubmit = async (values: FormikValues) => {
        await scheduleReport(values);
        invalidate("crons");
        onClose();
    };

    const handleRelativeRangeChanged = (rangeName: string, range: DateRange) => {
        setRangeName(rangeName);
        setDateRange(range);
        onDateChange(range);
    };

    const initialValues = getInitialValues(report.title, cron, seller.timezoneName);

    const datepickerPopoverProps = {
        placement: "auto",
    };

    return (
        <XolaFormik
            initialValues={initialValues}
            validationSchema={scheduleReportValidationSchema}
            onSubmit={handleSubmit}
        >
            {({ values, setFieldValue, handleSubmit }) => (
                <Modal isOpen={isOpen} onClose={onClose}>
                    <Modal.Header>Schedule Export</Modal.Header>
                    {firstStep && isEmpty(cron) ? (
                        <>
                            <Modal.Body>
                                <div className="mb-4 rounded-lg bg-red-lighter p-4">
                                    To schedule this report, you must select a relative date range.
                                </div>
                                <FormField name="dateRange" label="Date Range">
                                    <RelativeDateRange
                                        showApply={false}
                                        onChange={handleRelativeRangeChanged}
                                        onSubmit={handleRelativeRangeChanged}
                                    />
                                </FormField>
                            </Modal.Body>
                            <Modal.Footer>
                                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                                <SubmitButton disabled={isEmpty(dateRange)} onClick={() => setFirstStep(false)}>
                                    Next
                                </SubmitButton>
                            </Modal.Footer>
                        </>
                    ) : (
                        <>
                            <Modal.Body>
                                <div className="mb-4 rounded-lg bg-warning-lighter p-4">
                                    Scheduled Report will be sent in the account’s time zone{" "}
                                    {dayjs().tz(seller.timezoneName).format("z")}
                                </div>
                                <FormField name="reportName" label="Report Name">
                                    <Input readonly value={report.title} />
                                </FormField>
                                <FormField name="interval" label="Email Report Every">
                                    <div className="flex space-x-2">
                                        <InputField isRequired name="interval" value={values.interval} />
                                        <Select
                                            value={values.intervalType}
                                            onChange={async (e: any) => setFieldValue("intervalType", e.target.value)}
                                        >
                                            <option value={DurationUnit.DAY}>Day(s)</option>
                                            <option value={DurationUnit.WEEK}>Week(s)</option>
                                            <option value={DurationUnit.MONTH}>Month(s)</option>
                                            <option value={DurationUnit.YEAR}>Year(s)</option>
                                        </Select>
                                    </div>
                                </FormField>
                                <Label>Next Scheduled Run</Label>
                                <div className="flex space-x-2">
                                    <FormField label="Start Date" name="start" className="inline flex-1">
                                        <DatePickerPopover
                                            defaultInputPlaceholder="Start Date"
                                            value={values?.start ? formatDate(values.start) : null}
                                            popoverProps={datepickerPopoverProps}
                                            timezoneName={seller.timezoneName}
                                            month={values.start ? dayjs(values.start).toDate() : null}
                                            onChange={async (start: Dayjs) =>
                                                setFieldValue("start", dayjs(start).format(DateFormat.DATE_ISO))
                                            }
                                        />
                                    </FormField>
                                    <FormField className="inline w-50 flex-1" label="Start Time" name="time">
                                        <TimeslotTimePicker
                                            value={values.time ?? null}
                                            onChange={async (time: number) => setFieldValue("time", time)}
                                        />
                                    </FormField>
                                </div>
                            </Modal.Body>
                            <Modal.Footer>
                                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                                <SubmitButton isLoading={isSaving} onClick={handleSubmit}>
                                    Schedule
                                </SubmitButton>
                            </Modal.Footer>
                        </>
                    )}
                </Modal>
            )}
        </XolaFormik>
    );
};

const scheduleReportValidationSchema = object({
    interval: requiredNumberMoreThanZero(),
    start: requiredString(),
    time: requiredNumber(),
});

const getDuration = (duration: string) => duration.slice(1, -1);

const getDurationType = (duration: string) => duration.slice(-1);

const getInitialValues = (title: string, cron: Cron, timezoneName: string) => {
    const date = cron?.startAt ? utcToDate(cron.startAt, timezoneName) : null;

    return {
        title: title,
        interval: cron?.interval ? getDuration(cron.interval) : "",
        intervalType: cron?.interval ? getDurationType(cron.interval) : DurationUnit.DAY,
        start: date ? date.toDate() : "",
        time: date ? date.get("hours") * 100 + date.get("minutes") : null,
    };
};
